/**
 * @module features/client-device-management/components/OrgQuotasPanel
 * @description Per-device-type quota management panel and compact summary strip for
 * `{Org} Device Management`. Enables Super Admins to inspect current allocation headroom,
 * edit or configure maximum limits, and remove per-category quota caps across all
 * registered IoT gateway categories.
 */
import * as React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  AlertCircle,
  CheckCircle2,
  Gauge,
  Loader2,
  Pencil,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AppError } from "@/lib/api-client";
import {
  formatDeviceTypeLabel,
  QuotaItem,
  SetQuotaFormSchema,
  SetQuotaFormValues,
} from "../types";
import {
  useDeleteOrgDeviceQuota,
  useOrgDeviceQuotas,
  useSetOrgDeviceQuota,
} from "../api/quotaApi";

export interface QuotaProgressStripProps {
  quotas: QuotaItem[];
  isLoading: boolean;
  deviceTypes?: ReadonlyArray<{ type_id: string; label: string }>;
  onOpenQuotasTab: () => void;
}

/**
 * Compact glanceable strip displayed above the assigned-devices grid showing
 * real-time quota utilization per device type.
 */
export function QuotaProgressStrip({
  quotas,
  isLoading,
  deviceTypes,
  onOpenQuotasTab,
}: QuotaProgressStripProps) {
  if (isLoading) {
    return (
      <div
        aria-busy="true"
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
      >
        <Skeleton className="h-16 rounded-2xl" />
        <Skeleton className="h-16 rounded-2xl hidden sm:block" />
      </div>
    );
  }

  if (!quotas || quotas.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {quotas.map((quota) => {
        const pct = Math.min(
          100,
          Math.round(
            (quota.current_assigned_count / Math.max(1, quota.max_limit)) * 100
          )
        );
        const isAtLimit = quota.current_assigned_count >= quota.max_limit;
        const isNearLimit = !isAtLimit && pct >= 80;
        const fillClass = isAtLimit
          ? "bg-danger-solid"
          : isNearLimit
          ? "bg-warning-solid"
          : "bg-primary";

        return (
          <button
            key={quota.id}
            type="button"
            onClick={onOpenQuotasTab}
            className="text-left rounded-2xl border border-border/80 bg-card p-3.5 shadow-card hover:bg-muted/30 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            title="Click to edit device-type quotas"
          >
            <div className="flex items-center justify-between gap-2 text-xs">
              <span className="font-medium text-foreground truncate">
                {formatDeviceTypeLabel(quota.device_type, deviceTypes)}
              </span>
              <span
                className={`font-mono font-medium tabular-nums ${
                  isAtLimit
                    ? "text-danger"
                    : isNearLimit
                    ? "text-warning"
                    : "text-muted-foreground"
                }`}
              >
                {quota.current_assigned_count} / {quota.max_limit}
              </span>
            </div>
            <div className="mt-2 h-1.5 w-full rounded-full bg-muted overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-300 ${fillClass}`}
                style={{ width: `${pct}%` }}
              />
            </div>
          </button>
        );
      })}
    </div>
  );
}

export interface OrgQuotasPanelProps {
  clientId: string;
  orgName: string;
  deviceTypes: ReadonlyArray<{ type_id: string; label: string }>;
}

/**
 * Full quota inspection, editing, and removal panel inside `{Org} Device Management`.
 */
export function OrgQuotasPanel({
  clientId,
  orgName,
  deviceTypes,
}: OrgQuotasPanelProps) {
  const quotasQuery = useOrgDeviceQuotas(clientId);
  const setQuotaMutation = useSetOrgDeviceQuota(clientId);
  const deleteQuotaMutation = useDeleteOrgDeviceQuota(clientId);

  const [submitError, setSubmitError] = React.useState<AppError | null>(null);
  const [savedNotice, setSavedNotice] = React.useState<string | null>(null);
  const [editingQuotaId, setEditingQuotaId] = React.useState<string | null>(
    null
  );
  const [confirmDeleteQuota, setConfirmDeleteQuota] =
    React.useState<QuotaItem | null>(null);
  const [deletingId, setDeletingId] = React.useState<string | null>(null);

  const availableTypes = React.useMemo(() => {
    const map = new Map<string, string>();
    map.set("display_module", "Display Module");
    deviceTypes.forEach((dt) => map.set(dt.type_id, dt.label));
    (quotasQuery.data ?? []).forEach((q) => {
      if (!map.has(q.device_type)) {
        map.set(
          q.device_type,
          formatDeviceTypeLabel(q.device_type, deviceTypes)
        );
      }
    });
    return Array.from(map.entries()).map(([type_id, label]) => ({
      type_id,
      label,
    }));
  }, [deviceTypes, quotasQuery.data]);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<SetQuotaFormValues>({
    resolver: zodResolver(SetQuotaFormSchema) as any,
    defaultValues: {
      device_type: availableTypes[0]?.type_id ?? "display_module",
      max_limit: 10,
    },
  });

  const selectedDeviceType = watch("device_type");

  const onSubmit = async (values: SetQuotaFormValues): Promise<void> => {
    setSubmitError(null);
    setSavedNotice(null);
    try {
      const updated = await setQuotaMutation.mutateAsync(values);
      const label = formatDeviceTypeLabel(updated.device_type, availableTypes);
      setEditingQuotaId(null);
      setSavedNotice(`Quota for ${label} set to ${updated.max_limit} devices.`);
    } catch (rawErr: unknown) {
      setSubmitError(
        AppError.fromUnknown(rawErr, "Unable to update device-type quota.")
      );
    }
  };

  const handleConfirmDelete = async (quota: QuotaItem): Promise<void> => {
    setDeletingId(quota.id);
    setSubmitError(null);
    setSavedNotice(null);
    try {
      await deleteQuotaMutation.mutateAsync(quota.id);
      const label = formatDeviceTypeLabel(quota.device_type, availableTypes);
      setConfirmDeleteQuota(null);
      if (editingQuotaId === quota.id) {
        setEditingQuotaId(null);
      }
      setSavedNotice(`Removed quota cap for ${label}.`);
    } catch (rawErr: unknown) {
      setSubmitError(
        AppError.fromUnknown(rawErr, "Failed to remove device quota limit.")
      );
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">
      {/* Existing Quotas List */}
      <div className="lg:col-span-7 space-y-3">
        <div className="rounded-2xl border border-border/80 bg-card p-5 shadow-card space-y-4">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-tile text-tile-foreground shrink-0">
              <Gauge className="h-4 w-4" aria-hidden="true" />
            </div>
            <div>
              <h2 className="text-[15px] leading-5 font-medium text-foreground">
                Active Device-Type Quotas
              </h2>
              <p className="text-xs text-muted-foreground">
                Enforced automatically during single, batch, and CSV assignments
                for {orgName}.
              </p>
            </div>
          </div>

          {quotasQuery.isLoading ? (
            <div className="space-y-3" aria-busy="true">
              <Skeleton className="h-16 w-full rounded-xl" />
              <Skeleton className="h-16 w-full rounded-xl" />
            </div>
          ) : quotasQuery.isError ? (
            <div
              role="alert"
              className="rounded-xl border border-danger/20 bg-danger-soft p-4 text-xs text-danger space-y-2"
            >
              <p>
                {AppError.fromUnknown(quotasQuery.error).errorDetails ||
                  "Failed to load quotas."}
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void quotasQuery.refetch()}
                className="bg-card text-foreground text-xs"
              >
                Retry
              </Button>
            </div>
          ) : (quotasQuery.data ?? []).length === 0 ? (
            <div className="rounded-xl border border-dashed border-border p-6 text-center">
              <p className="text-sm font-medium text-foreground">
                No per-type quota limits configured yet
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Use the form to set maximum device allocations per hardware
                category.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {(quotasQuery.data ?? []).map((quota) => {
                const pct = Math.min(
                  100,
                  Math.round(
                    (quota.current_assigned_count /
                      Math.max(1, quota.max_limit)) *
                      100
                  )
                );
                const isAtLimit =
                  quota.current_assigned_count >= quota.max_limit;
                const label = formatDeviceTypeLabel(
                  quota.device_type,
                  availableTypes
                );
                const isEditingThis = editingQuotaId === quota.id;
                const isConfirming = confirmDeleteQuota?.id === quota.id;

                return (
                  <div
                    key={quota.id}
                    className={`rounded-xl border p-3.5 space-y-2 transition-colors ${
                      isEditingThis
                        ? "border-primary bg-primary/5 ring-1 ring-primary/30"
                        : "border-border/70 bg-muted/20"
                    }`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          {label}
                        </p>
                        <p className="font-mono text-[11px] text-muted-foreground">
                          {quota.device_type}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span
                          className={`rounded-full px-2.5 py-0.5 text-[11px] font-mono font-medium border ${
                            isAtLimit
                              ? "bg-danger-soft text-danger border-danger/20"
                              : "bg-success-soft text-success border-success/20"
                          }`}
                        >
                          {quota.current_assigned_count} / {quota.max_limit}{" "}
                          assigned
                        </span>

                        {!isConfirming ? (
                          <>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setEditingQuotaId(quota.id);
                                setConfirmDeleteQuota(null);
                                setValue("device_type", quota.device_type, {
                                  shouldValidate: true,
                                });
                                setValue("max_limit", quota.max_limit, {
                                  shouldValidate: true,
                                });
                              }}
                              aria-label={`Edit quota for ${label}`}
                              className="h-8 touch:min-h-[44px] px-2.5 text-xs gap-1 bg-card"
                            >
                              <Pencil
                                className="h-3.5 w-3.5"
                                aria-hidden="true"
                              />
                              <span>Edit</span>
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => setConfirmDeleteQuota(quota)}
                              aria-label={`Remove quota for ${label}`}
                              className="h-8 w-8 touch:min-h-[44px] touch:min-w-[44px] text-muted-foreground hover:text-danger hover:bg-danger-soft"
                            >
                              <Trash2
                                className="h-4 w-4"
                                aria-hidden="true"
                              />
                            </Button>
                          </>
                        ) : (
                          <div className="flex items-center gap-1.5">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={deletingId === quota.id}
                              onClick={() => setConfirmDeleteQuota(null)}
                              className="h-8 touch:min-h-[44px] px-2.5 text-xs"
                            >
                              Cancel
                            </Button>
                            <Button
                              type="button"
                              variant="destructive"
                              size="sm"
                              disabled={deletingId === quota.id}
                              onClick={() => void handleConfirmDelete(quota)}
                              className="h-8 touch:min-h-[44px] px-2.5 text-xs"
                            >
                              {deletingId === quota.id ? (
                                <Loader2
                                  className="h-3.5 w-3.5 animate-spin"
                                  aria-hidden="true"
                                />
                              ) : (
                                "Remove"
                              )}
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${
                          isAtLimit ? "bg-danger-solid" : "bg-primary"
                        }`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Set / Update Quota Form */}
      <div className="lg:col-span-5">
        <form
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          className="rounded-2xl border border-border/80 bg-card p-5 shadow-card space-y-4"
        >
          <div>
            <h3 className="text-[15px] leading-5 font-medium text-foreground">
              {editingQuotaId ? "Edit Quota Limit" : "Configure Quota Limit"}
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Set or update the maximum active devices permitted for a gateway
              type.
            </p>
          </div>

          {submitError && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-xl border border-danger/20 bg-danger-soft p-3 text-xs text-danger"
            >
              <AlertCircle
                className="h-4 w-4 shrink-0 mt-0.5"
                aria-hidden="true"
              />
              <div className="min-w-0">
                <p>{submitError.errorDetails}</p>
                {submitError.requestId && (
                  <p className="mt-1 font-mono text-[11px] text-muted-foreground break-all">
                    Request ID: {submitError.requestId}
                  </p>
                )}
              </div>
            </div>
          )}

          {savedNotice && (
            <div
              role="status"
              className="flex items-center gap-2 rounded-xl border border-success/20 bg-success-soft p-3 text-xs text-success"
            >
              <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{savedNotice}</span>
            </div>
          )}

          <fieldset className="space-y-4">
            <legend className="sr-only">Configure device-type quota</legend>

            <div className="space-y-1.5">
              <label
                id="quota-device-type-label"
                className="text-xs font-medium text-foreground block"
              >
                Gateway Type <span className="text-danger">*</span>
              </label>
              <Select
                value={selectedDeviceType}
                onValueChange={(val) => {
                  setEditingQuotaId(null);
                  setValue("device_type", val, { shouldValidate: true });
                }}
              >
                <SelectTrigger
                  aria-labelledby="quota-device-type-label"
                  className="h-11 sm:h-10 bg-card rounded-xl"
                >
                  <SelectValue placeholder="Select gateway type" />
                </SelectTrigger>
                <SelectContent>
                  {availableTypes.map((dt) => (
                    <SelectItem key={dt.type_id} value={dt.type_id}>
                      {dt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.device_type && (
                <p role="alert" className="text-[11px] text-danger">
                  {errors.device_type.message}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <label
                htmlFor="quota-max-limit"
                className="text-xs font-medium text-foreground block"
              >
                Maximum Active Devices <span className="text-danger">*</span>
              </label>
              <Input
                id="quota-max-limit"
                type="number"
                inputMode="numeric"
                min={1}
                {...register("max_limit")}
                className="h-11 sm:h-10 text-base sm:text-sm bg-card tabular-nums"
              />
              {errors.max_limit && (
                <p role="alert" className="text-[11px] text-danger">
                  {errors.max_limit.message}
                </p>
              )}
            </div>
          </fieldset>

          <Button
            type="submit"
            disabled={setQuotaMutation.isPending}
            className="w-full h-11 sm:h-10 touch:min-h-[44px] text-xs"
          >
            {setQuotaMutation.isPending ? (
              <>
                <Loader2
                  className="mr-2 h-4 w-4 animate-spin"
                  aria-hidden="true"
                />
                <span>Saving Quota...</span>
              </>
            ) : (
              <span>Save Quota Limit</span>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
